import { useEffect, useMemo, useState } from "react";
import { format, subDays, startOfMonth } from "date-fns";
import { api, apiError, ROLE_LABEL, fmtDateTime } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollText, X, Download } from "lucide-react";
import { toast } from "sonner";
import { TablePager } from "@/components/TablePager";
import { DateField } from "@/components/DateField";

const ACTION_COLORS = {
  claim_submitted: "bg-info/10 text-info border-info/30",
  claim_approved: "bg-success/10 text-success border-success/30",
  claim_rejected: "bg-destructive/10 text-destructive border-destructive/30",
  claim_forwarded: "bg-warning/10 text-warning border-warning/30",
  user_created: "bg-success/10 text-success border-success/30",
  user_deleted: "bg-destructive/10 text-destructive border-destructive/30",
  user_disabled: "bg-muted text-muted-foreground",
  user_enabled: "bg-success/10 text-success border-success/30",
  tenant_deleted: "bg-destructive/10 text-destructive border-destructive/30",
  impersonation_started: "bg-warning/10 text-warning border-warning/30",
};

const fmtAction = (a) => a.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const fmtTime = fmtDateTime;
const toKey = (d) => (d ? format(d, "yyyy-MM-dd") : "");

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("all");
  const [from, setFrom] = useState(undefined);
  const [to, setTo] = useState(undefined);
  const [page, setPage] = useState(1);
  const pageSize = 15;

  useEffect(() => {
    api.get("/logs")
      .then(({ data }) => setLogs(data))
      .catch((err) => toast.error(apiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const actionTypes = useMemo(
    () => Array.from(new Set(logs.map((l) => l.action))).sort(),
    [logs]
  );

  const filtered = useMemo(() => {
    const fromKey = toKey(from);
    const toKeyStr = toKey(to);
    return logs.filter((l) => {
      if (action !== "all" && l.action !== action) return false;
      const d = (l.created_at || "").slice(0, 10);
      if (fromKey && d < fromKey) return false;
      if (toKeyStr && d > toKeyStr) return false;
      return true;
    });
  }, [logs, action, from, to]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const hasFilters = action !== "all" || from || to;
  const reset = () => { setAction("all"); setFrom(undefined); setTo(undefined); setPage(1); };

  const applyPreset = (preset) => {
    const today = new Date();
    if (preset === "7d") { setFrom(subDays(today, 6)); setTo(today); }
    else if (preset === "month") { setFrom(startOfMonth(today)); setTo(today); }
    setPage(1);
  };

  const exportCsv = () => {
    if (filtered.length === 0) { toast.error("Nothing to export"); return; }
    const header = ["When", "Actor", "Role", "Action", "Details"];
    const rows = filtered.map((l) => [
      fmtTime(l.created_at), l.actor_name || "", ROLE_LABEL[l.actor_role] || l.actor_role || "",
      fmtAction(l.action), l.description || "",
    ]);
    const esc = (c) => `"${String(c ?? "").replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity-log-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} entr${filtered.length === 1 ? "y" : "ies"}`);
  };

  return (
    <div className="space-y-4" data-testid="logs-page">
      <p className="text-sm text-muted-foreground">
        Activity across your organization — claims, users, categories and currencies.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Action</label>
          <Select value={action} onValueChange={(v) => { setAction(v); setPage(1); }}>
            <SelectTrigger className="w-52 rounded-sm" data-testid="log-filter-action"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {actionTypes.map((a) => <SelectItem key={a} value={a}>{fmtAction(a)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">From</label>
          <DateField value={from} onChange={(d) => { setFrom(d); setPage(1); }} placeholder="Start date" testId="log-filter-from" />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">To</label>
          <DateField value={to} onChange={(d) => { setTo(d); setPage(1); }} placeholder="End date" testId="log-filter-to" />
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => applyPreset("7d")} data-testid="log-preset-7d">Last 7 days</Button>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => applyPreset("month")} data-testid="log-preset-month">This month</Button>
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" className="rounded-sm" onClick={reset} data-testid="log-filter-clear">
            <X className="h-4 w-4 mr-1.5" />Clear
          </Button>
        )}
        <div className="ml-auto">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={exportCsv} data-testid="log-export-csv">
            <Download className="h-4 w-4 mr-1.5" />Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <ScrollText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">{logs.length === 0 ? "No activity recorded yet." : "No activity matches your filters."}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.map((l) => (
                <TableRow key={l.id} className="hover:bg-muted/50" data-testid={`log-row-${l.id}`}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtTime(l.created_at)}</TableCell>
                  <TableCell className="text-sm">
                    <span className="font-medium">{l.actor_name}</span>
                    <span className="text-xs text-muted-foreground ml-1">({ROLE_LABEL[l.actor_role] || l.actor_role})</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`rounded-full ${ACTION_COLORS[l.action] || "bg-muted text-muted-foreground"}`}>
                      {fmtAction(l.action)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{l.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TablePager page={safePage} pageCount={pageCount} total={filtered.length} shown={paged.length}
          onChange={setPage} testId="logs-pager" />
      </div>
    </div>
  );
}
